// src/components/services/AEPS.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link
import {
    MdOutlineFingerprint,
    MdOutlineAccountBalanceWallet,
    MdOutlineSecurity,
    MdOutlineDescription,
    MdOutlineVerifiedUser,
} from "react-icons/md";
import { FaPhoneAlt, FaHandshake, FaPiggyBank } from "react-icons/fa";


function AEPS() {
    const serviceName = "AEPS"; // Set service name

    // Animation Variants (Keep or move to utils/animations.js)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2, // Stagger animation of child elements
            },
        },
    };
    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <section className="py-16 md:py-24 px-4 md:px-8 bg-gray-50 text-gray-700">
            <div className="container mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 text-center"
                    >
                        Aadhaar Enabled Payment System (AEPS)
                    </motion.h1>
                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-600 mb-10 md:mb-12 text-center max-w-3xl mx-auto"
                    >
                        Access your bank account securely and conveniently using just your Aadhaar and fingerprint. Keziah Global Private Limited, in partnership with Fino Payments Bank, brings you easy access to essential banking services through AEPS.
                    </motion.p>

                    {/* Features and Benefits */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Key Features
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Feature 1 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineFingerprint className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Aadhaar-Enabled
                                    </h4>
                                    <p className="text-gray-600">
                                        Link your bank account to your Aadhaar for secure and convenient transactions.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 2 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineAccountBalanceWallet className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                       Withdraw & Deposit Cash
                                    </h4>
                                    <p className="text-gray-600">
                                       Easily withdraw or deposit cash at any Keziah-Fino authorized agent using your fingerprint.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 3 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineSecurity className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Check Your Balance
                                    </h4>
                                    <p className="text-gray-600">
                                        Instantly check your bank account balance with a simple fingerprint scan.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 4 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.3 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaPiggyBank className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                       Transfer Money
                                    </h4>
                                    <p className="text-gray-600">
                                       Send money to any Aadhaar-linked bank account securely.

                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 5 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.4 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaHandshake className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Easy and Accessible
                                    </h4>
                                    <p className="text-gray-600">
                                        No need for bank account details or debit cards. Access services with just your Aadhaar.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 6 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.5 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineVerifiedUser className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Safe and Secure
                                    </h4>
                                    <p className="text-gray-600">
                                      Your transactions are protected by biometric authentication, ensuring maximum security.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* How to Use */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            How to Use AEPS
                        </h3>
                        <ol className="list-decimal pl-8 space-y-4 text-gray-600 text-lg">
                            <li>Visit Keziah Global Private Limited authorized agent location.</li>
                            <li>Provide your Aadhaar number and select the desired service (cash withdrawal, deposit, etc.).</li>
                            <li>Verify your identity with a quick fingerprint scan.</li>
                            <li>Complete your transaction securely and conveniently.</li>
                        </ol>
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                           Ready to Experience the Convenience of AEPS?
                        </h3>
                        <p className="text-gray-600 text-lg text-center">
                        Visit our branch in Salem, Tamil Nadu, to get started. Our representatives are ready to assist you. You can also request a callback to learn more about how AEPS can simplify your banking experience.
                        </p>
                        <motion.div
                            variants={itemVariants}
                            className="mt-10 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
                        >
                            <a
                                href="tel:9043588446"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaPhoneAlt className="inline-block mr-2" />
                                Call Now
                            </a>
                            {/* Use Link for Callback */}
                            <Link to={`/callback?serviceName=${encodeURIComponent(serviceName)}`}>
                                <motion.button
                                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Request a Callback
                                </motion.button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default AEPS;